import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { addWatermark } from '../services/pdfService';
import { DownloadIcon, WatermarkIcon, SpinnerIcon, ErrorIcon } from './icons';
import { useTranslation } from '../i18n';

const WatermarkTool: React.FC = () => {
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null);
  const [position, setPosition] = useState('C');
  const [size, setSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleDocumentSelect = useCallback((files: File[]) => {
    if (files.length > 0) setDocumentFile(files[0]);
    setError(null);
    setResultUrl(null);
  }, []);

  const handleWatermarkSelect = useCallback((files: File[]) => {
    if (files.length > 0) setWatermarkFile(files[0]);
    setError(null);
    setResultUrl(null);
  }, []);

  const handleAddWatermark = async () => {
    if (!documentFile) {
      setError(t('errorSelectDocument'));
      return;
    }
    if (!watermarkFile) {
      setError(t('errorSelectWatermark'));
      return;
    }

    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setResultUrl(null);

    try {
      const watermarkedBlob = await addWatermark(documentFile, watermarkFile, position, size, setProgress);
      setIsProcessing(true);
      const url = URL.createObjectURL(watermarkedBlob);
      setResultUrl(url);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };
  
  const positions = [
    { id: 'TL', label: 'Top-Left' }, { id: 'T', label: 'Top-Center' }, { id: 'TR', label: 'Top-Right' },
    { id: 'L', label: 'Middle-Left' }, { id: 'C', label: 'Center' }, { id: 'R', label: 'Middle-Right' },
    { id: 'BL', label: 'Bottom-Left' }, { id: 'B', label: 'Bottom-Center' }, { id: 'BR', label: 'Bottom-Right' },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-3 text-center">{t('watermarkUploadDoc')}</h3>
          <FileUpload onFilesSelect={handleDocumentSelect} multiple={false} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
          {documentFile && <FileList files={[documentFile]} onRemoveFile={() => setDocumentFile(null)} />}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-3 text-center">{t('watermarkUploadWatermark')}</h3>
          <FileUpload onFilesSelect={handleWatermarkSelect} multiple={false} accept="image/jpeg,image/png" />
          {watermarkFile && <FileList files={[watermarkFile]} onRemoveFile={() => setWatermarkFile(null)} />}
        </div>
      </div>
      
      {documentFile && watermarkFile && (
        <div className="mt-6 pt-6 border-t border-slate-700">
          <h3 className="text-lg font-semibold text-slate-200 mb-3">{t('watermarkOptions')}</h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('watermarkPositionLabel')}</label>
            <div className="grid grid-cols-3 gap-2">
              {positions.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setPosition(id)}
                  aria-label={label}
                  className={`py-2 px-3 rounded-md text-sm transition-colors ${position === id ? 'bg-indigo-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="size-slider" className="block text-sm font-medium text-slate-300 mb-2">
              {t('watermarkSizeLabel')} <span className="font-bold text-indigo-400">{size}%</span>
            </label>
            <input
              id="size-slider"
              type="range"
              min="1"
              max="100"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      )}

      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}
      
      <div className="mt-6">
        <button
          onClick={handleAddWatermark}
          disabled={isLoading || !documentFile || !watermarkFile}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <WatermarkIcon />}
          <span>{isLoading ? (isProcessing ? t('statusProcessing') : t('statusUploading')) : t('watermarkButton')}</span>
        </button>
      </div>

      {error && (
         <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {resultUrl && (
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">{t('successTitle')}</h3>
          <p className="text-sm mb-4">{t('successWatermarkMessage')}</p>
          <a
            href={resultUrl}
            download={documentFile?.name.replace(/(\.[\w\d_-]+)$/i, '_watermarked$1') || 'watermarked_file'}
            className="flex w-full items-center justify-center gap-3 bg-green-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 transition-all duration-200"
          >
            <DownloadIcon/>
            {t('downloadWatermarkedButton')}
          </a>
        </div>
      )}
    </div>
  );
};

export default WatermarkTool;
